import React, { useState } from "react";
import styled from "styled-components";
import emailjs from "emailjs-com";
import { Email, Person, Message } from "@mui/icons-material";

const PageContainer = styled.div`
  background-color: var(--color-bg);
  min-height: 90vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 120px 24px;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 600px;
`;

const Title = styled.h1`
  font-family: var(--font-serif);
  font-size: 3rem;
  margin-bottom: 16px;
  text-align: center;
  color: var(--color-text);
`;

const Subtitle = styled.p`
  text-align: center;
  color: var(--color-text-light);
  margin-bottom: 48px;
  font-size: 1.1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InputGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const IconWrapper = styled.div`
  position: absolute;
  left: 16px;
  color: var(--color-text-light);
  opacity: 0.5;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 16px 16px 48px;
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 8px;
  font-family: var(--font-sans);
  font-size: 1rem;
  background: white;
  transition: all 0.2s;
  outline: none;

  &:focus {
    border-color: var(--color-primary-blue);
    box-shadow: 0 0 0 3px rgba(2, 74, 159, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 16px 16px 16px 48px;
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 8px;
  font-family: var(--font-sans);
  font-size: 1rem;
  background: white;
  transition: all 0.2s;
  outline: none;
  min-height: 150px;
  resize: vertical;

  &:focus {
    border-color: var(--color-primary-blue);
    box-shadow: 0 0 0 3px rgba(2, 74, 159, 0.1);
  }
`;

const SubmitButton = styled.button`
  background-color: var(--color-primary-blue);
  color: white;
  padding: 16px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-family: var(--font-sans);

  &:hover {
    background-color: var(--color-accent);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  text-align: center;
  font-size: 0.9rem;
`;

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await emailjs.sendForm(
        "service_58ffkzo",
        "template_95p1inb",
        e.target,
        "2H6D_y2R7lZS8rRMk"
      );
      setFormData({
        name: "",
        email: "",
        message: ""
      });
      setError(null);
      alert("Message sent successfully!");
    } catch (error) {
      console.error("Form submission failed:", error);
      setError("An error occurred. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <ContentWrapper>
        <Title>Get in Touch</Title>
        <Subtitle>
          Have a question or want to connect? We'd love to hear from you.
        </Subtitle>

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <IconWrapper><Person /></IconWrapper>
            <Input
              type="text"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </InputGroup>

          <InputGroup>
            <IconWrapper><Email /></IconWrapper>
            <Input
              type="email"
              name="email"
              placeholder="Your Email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </InputGroup>

          <InputGroup style={{ alignItems: 'flex-start' }}>
            <IconWrapper style={{ top: '16px' }}><Message /></IconWrapper>
            <TextArea
              name="message"
              placeholder="Your Message"
              value={formData.message}
              onChange={handleChange}
              required
            />
          </InputGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? "Sending..." : "Send Message"}
          </SubmitButton>
        </Form>
      </ContentWrapper>
    </PageContainer>
  );
};

export default ContactUs;